import { Inject, Injectable, Injector } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { from, of, zip } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationData } from '@models/*';
import { AddNotification, CompleteAppInit, CoreActionTypes, InitAuth, InitDriverData, InitLang, InitOfflineFlow, InitOnlineFlow, OfflineHydrateStore, SaveDriverName, SetOfflineStatus, StoreDriver, StoreToken, StoreUserProfile, WakeUpServiceWorkers } from '../actions';
import { getDriver, getOfflineStatus } from '../selectors';
import { AppState, getRouterState } from '../registration';
import { NetworkService } from '../../core/services/network.service';
import { offlineQueueDB } from '../../../service-worker/offline-queue.database.typed';
import { AuthService } from '@auth0/auth0-angular';
import { UserService } from '../../services/user.service';
import { LocalStorageUtil } from '../../shared/utilities/local-storage.util';
import { ServiceWorkerService } from '../../core/services/service-worker.service';
import { OpcoService } from '../../services/opco.service';
import { APP_CONFIG, AppConfig } from '../../config/app-config';
import { parseLocaleOrUseDefault } from '../../base/entities/locale';
import * as Sentry from "@sentry/angular";
import { NewFirebaseMessagingService } from '../../core/services/new-firebase-messaging.service';

@Injectable()
export class InitializationEffects {
  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly networkService: NetworkService,
    private readonly translateService: TranslateService,
    private readonly newFirebaseMessagingService: NewFirebaseMessagingService,
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly injector: Injector
  ) {}

  /**
   * Use an {@link Injector} to load the {@link AuthService}. If the AuthService is directly injected during
   * construction it will fail to load because the {@link Auth0Client} is not yet initialized. This is due to
   * this component being loaded before the {@link initializeAuth0Client} method is executed.
   */
  private authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private userService(): UserService {
    return this.injector.get(UserService);
  }

  private opcoService(): OpcoService {
    return this.injector.get(OpcoService);
  }

  private serviceWorkerService(): ServiceWorkerService {
    return this.injector.get(ServiceWorkerService);
  }

  initApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.INIT_APP),

      mergeMap(() => {
        const isOnline = this.networkService.checkOnlineStatus();
        const actionsArray: any[] = [new WakeUpServiceWorkers(), new SetOfflineStatus(!isOnline)];
        const savedName = LocalStorageUtil.getItemObject('savedName');
        if (savedName) {
          /**
           * The below action might seem irrelevant since the driver name is already stored in
           * localStorage but the actual purpose of this action is to trigger the {@link coreReducer}
           * such that the driver name is also stored in the NgRx {@link CoreState}.
           */
          actionsArray.push(new SaveDriverName(savedName));
        }

        if (isOnline) {
          actionsArray.push(new InitOnlineFlow());
        } else {
          actionsArray.push(new InitOfflineFlow());
        }
        return actionsArray;
      })
    )
  );

  initOnlineFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.INIT_ONLINE_FLOW),
      switchMap(() => from(this.authService().isAuthenticated$)),
      withLatestFrom(this.store.select(getRouterState)),
      mergeMap(([isAuth, routerState]) => {
        const actionsArray: any[] = [];

        if (isAuth) {
          actionsArray.push(new InitAuth());
        } else {
          const redirectUrlAfterLogin = routerState.state.url;
          LocalStorageUtil.setItemObject('redirectRoute', redirectUrlAfterLogin);
        }

        return actionsArray;
      })
    )
  );

  // todo beforeunload remove and add userProfile

  initOfflineFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.INIT_OFFLINE_FLOW),
      mergeMap(() => {
        const profile = LocalStorageUtil.getItemObject('userProfile');
        const driver = LocalStorageUtil.getItemObject('driver');
        return [new StoreUserProfile(profile), new StoreDriver(driver)];
      })
    )
  );

  initAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.INIT_AUTH),
      mergeMap(() => zip(this.authService().user$, this.authService().getAccessTokenSilently())),
      mergeMap(([user, token]) => {
        return [new StoreUserProfile(user), new StoreToken(token)];
      })
    )
  );

  finalizeInitBranching$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.STORE_USER_PROFILE),
      mergeMap(() => this.store.select(getOfflineStatus).pipe(take(1))),
      mergeMap((isOffline) => {
        const actionsArray: any[] = [new InitLang()];

        if (isOffline) {
          actionsArray.push(new OfflineHydrateStore());
        } else if (this.userService().user().hasDriverRole()) {
          actionsArray.push(new InitDriverData());
        }

        return actionsArray;
      })
    )
  );

  wakeUpServiceWorkers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionTypes.WAKE_UP_SERVICE_WORKERS),
        tap((action: WakeUpServiceWorkers) => this.serviceWorkerService().wakeUp())
      ),
    { dispatch: false }
  );

  handleSentryInitSW$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionTypes.STORE_DRIVER),
        map((action: StoreDriver) => action.payload),
        tap((driver) => LocalStorageUtil.setItemObject('driver', driver)),
        tap(async (driver) => {
          const sentryUser = driver && {
            id: driver.internalId,
            username: driver.name,
            email: driver.login,
            opco: driver.opco
          };

          const { sentry } = this.appConfig;
          if (sentry.enabled) {
            Sentry.withScope((scope) => scope.setUser(sentryUser));
          }
        })
      ),
    { dispatch: false }
  );

  initLang$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.INIT_LANG),
      mergeMap(() => this.store.select(getDriver)),
      filter((driver) => !!driver),
      tap((driver) => {
        this.translateService.setDefaultLang(parseLocaleOrUseDefault(driver.locale));
      }),
      map(() => new CompleteAppInit())
    )
  );
  checkVersionIncompatibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.COMPLETE_APP_INIT),
      mergeMap(() => {
        const actionsArray: any[] = [];

        let requiresLogout = false;

        if (!offlineQueueDB.protocolPhotoUploads || !offlineQueueDB.photoRequests) {
          requiresLogout = true;
        }

        if (requiresLogout) {
          const notification: NotificationData = {
            type: 'app-require-logout',
            targetId: null,
            title: this.translateService.instant('NOTIFICATIONS.APP_REQUIRE_LOGOUT_TITLE'),
            body: this.translateService.instant('NOTIFICATIONS.APP_REQUIRE_LOGOUT_BODY'),
            button: {
              text: this.translateService.instant('NOTIFICATIONS.APP_REQUIRE_LOGOUT_BUTTON'),
              url: null
            }
          };

          console.info(`Incompatible version detected on device after page refresh; logout required`);
          actionsArray.push(new AddNotification({ notification, duringOffline: false }));
        }

        return actionsArray;
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionTypes.LOGOUT),
        tap(() => LocalStorageUtil.removeItem('dataBank')),
        tap(() => LocalStorageUtil.removeItem('savedName')),
        switchMap(() =>
          this.newFirebaseMessagingService.unsubscribeToken().pipe(
            catchError((error) => {
              console.error(
                'Ignoring error while unsubscribing from notifications to prevent blocking of the underlying logout flow.',
                error
              );
              return of(false);
            })
          )
        ),
        tap(() => this.serviceWorkerService().unregisterAllRegistrations().subscribe()),
        tap(() => {
          this.userService().clear();
          this.opcoService().clear();

          this.authService().logout({
            logoutParams: {
              returnTo: window.location.origin
            }
          });
        })
      ),
    { dispatch: false }
  );
}